'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import type { PaymentsResponse } from '@//app/api/commerce/payments/[id]/list';
import { Link } from '@//components/Link';
import usePayments, { type UsePaymentsVariables } from '@//data/ecom/usePayments';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import styles from './AsideActionCard.module.scss';
import FinanceSectionSkeleton from './FinanceSectionSkeleton';

interface FinanceSectionProps {
  itemId: string;
  branchId: string;
  financeSectionId: string;
  paymentsResponse?: PaymentsResponse;
  onEmpty?: () => void;
}

const FinanceSection: React.FC<FinanceSectionProps> = ({
  financeSectionId,
  paymentsResponse,
  itemId,
  branchId,
  onEmpty,
}) => {
  const t = useTranslations('pages.item.informationSection');
  const locale = useLocale();
  const marketMetadata = useMarketMetadata();

  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const paymentsVariables = useMemo(
    (): UsePaymentsVariables => ({
      id: itemId,
      branchId,
    }),
    [itemId, branchId],
  );

  const paymentsQuery = usePayments({
    variables: paymentsVariables,
    initialData: paymentsResponse,
  });
  const payments = paymentsQuery.data?.response;
  const payment = payments?.[0];

  // Determine loading/error states
  const isLoading = !isHydrated || paymentsQuery.isLoading;
  const hasError = paymentsQuery.isError;
  const hasPaymentData = payments && payments.length > 0;

  // Notify parent when there's no data to render
  useEffect(() => {
    if (!isLoading && (hasError || !hasPaymentData)) {
      onEmpty?.();
    }
  }, [isLoading, hasError, hasPaymentData, onEmpty]);

  // Show skeleton during loading
  if (isLoading) {
    return <FinanceSectionSkeleton />;
  }

  // Hide if error or no payment data
  if (hasError || !hasPaymentData) {
    return null;
  }

  const monthlyCost = formatCurrency(payment!.monthlyCost, {
    locale,
    currency: marketMetadata.currency,
    period: 'month',
  });

  return (
    <li
      className={styles.priceItem}
      aria-label={t('finance.ariaLabel', {
        price: monthlyCost,
      })}
    >
      <Link
        href={`#${financeSectionId}`}
        title={t('finance.title')}
        className={styles.priceLabel}
        prefetch="none"
      >
        {t('finance.label')}
      </Link>
      <span className={styles.priceValue}>{monthlyCost}</span>
    </li>
  );
};

export default FinanceSection;
